import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoWarehouses from "config/demo/warehouses.json";
import WarehouseForm from "components/agro/warehouses/WarehouseForm";

const Warehouses = () => {
  const [warehouses] = useState(demoWarehouses.slice(0, 10));
  const [warehouseDetails, setWarehouseDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      warehouses.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={warehouseDetails?.id === item.id ? strings.warehouses.hide : strings.warehouses.show}
              icon={warehouseDetails?.id === item.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                warehouseDetails?.id === item.id ? setWarehouseDetails(null) : setWarehouseDetails(item);
              }}
            />
            <ActionIcon
              name={strings.warehouses.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.warehouses.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.customName || item.warehouseNumber,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [warehouses, warehouseDetails?.id]
  );

  const openModal = (action, id = null) => setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    { key: "customName", label: strings.table.headers.customName, _style: { width: "40%" } },
    { key: "warehouseNumber", label: strings.table.headers.warehouseNumber, _style: { width: "20%" } },
    { key: "sizeM2", label: strings.table.headers.sizeM2, _style: { width: "20%" } },
    { key: "actions", label: "", _style: { width: "20%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.warehouses.addWarehouse}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField label={strings.table.search.label} placeholder={strings.table.search.placeholder} />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!warehouseDetails?.id} style={styles.collapseCard}>
          <CCard key={warehouseDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{warehouseDetails?.customName || warehouseDetails?.warehouseNumber}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.customName}:</b> {warehouseDetails?.customName ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.warehouseNumber}:</b> {warehouseDetails?.warehouseNumber ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.farmId}:</b> {warehouseDetails?.farmId ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.sizeM2}:</b> {warehouseDetails?.sizeM2 ?? "-"} m²
              </p>

              <p className="text-muted">
                <b>{strings.details.location}:</b>{" "}
                {warehouseDetails?.location?.placePoint ? warehouseDetails.location.placePoint : warehouseDetails?.location ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.storageZones}:</b>{" "}
                {(Array.isArray(warehouseDetails?.storageZones) ? warehouseDetails.storageZones.join(", ") : warehouseDetails?.storageZones) ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.shelves}:</b> {warehouseDetails?.shelves ? JSON.stringify(warehouseDetails.shelves) : "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {warehouseDetails?.createdAt ? moment(warehouseDetails.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, { name: modalDetails.itemName })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal size="xl" alignment="center" visible={modalForm.visible} onClose={() => setModalForm({ visible: false })} backdrop="static">
          <CModalHeader>
            <CModalTitle>{modalForm.action === "create" ? strings.modalForm.addTitle : strings.modalForm.updateTitle}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <WarehouseForm
                type={modalForm.action}
                initialValues={
                  modalForm.action === "update" ? warehouses.find((l) => l.id === modalForm.itemId) || {} : {}
                }
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Almacenes",
    warehouses: {
      addWarehouse: "Agregar Almacén",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        customName: "Nombre",
        warehouseNumber: "No. Almacén",
        sizeM2: "Tamaño (m²)",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Almacén",
      customName: "Nombre",
      warehouseNumber: "Número de Almacén",
      farmId: "Finca (ID)",
      sizeM2: "Tamaño (m²)",
      location: "Ubicación / Punto clave",
      storageZones: "Zonas de Almacenamiento",
      shelves: "Estanterías",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el almacén {name}?",
    },
    modalForm: {
      addTitle: "Agregar Almacén",
      updateTitle: "Actualizar Almacén",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default Warehouses;
